import { useState, useEffect, lazy, Suspense } from 'react';
import { useAuth } from '../contexts/AuthContext';
import api from '../utils/api';
import '../components/home/home-theme.css';

const HeroSection = lazy(() => import('../components/home/HeroSection'));
const LiveStatsBar = lazy(() => import('../components/home/LiveStatsBar'));
const FeaturesSection = lazy(() => import('../components/home/FeaturesSection'));
const ShowcaseSection = lazy(() => import('../components/home/ShowcaseSection'));
const HowItWorks = lazy(() => import('../components/home/HowItWorks'));
const LeaderboardPreview = lazy(() => import('../components/home/LeaderboardPreview'));
const TestimonialsSection = lazy(() => import('../components/home/TestimonialsSection'));
const CTASection = lazy(() => import('../components/home/CTASection'));
const Footer = lazy(() => import('../components/home/Footer'));
const FloatingLeaderboard = lazy(() => import('../components/home/FloatingLeaderboard'));

const SectionSkeleton = ({ height = 'h-96' }) => (
  <div className={`${height} w-full bg-gradient-to-r from-gray-800/20 via-gray-700/20 to-gray-800/20 animate-pulse rounded-2xl`} />
);

/**
* Renders the home page layout, fetching leaderboard stats and displaying the composed sections.
* @example
* Home()
* <div className="min-h-screen ...">...</div>
* @param {{void}} none - This component accepts no props.
* @returns {{JSX.Element}} The rendered home page element.
*/
const Home = () => {
  const { currentUser } = useAuth();

  // Set page title
  useEffect(() => {
    document.title = 'CodeBattles - Competitive Programming Platform';
  }, []);
  const [stats, setStats] = useState({
    totalUsers: 2384,
    totalProblems: 450,
    totalSubmissions: 58220,
    activeBattles: 124
  });
  const [leaderboard, setLeaderboard] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchHomeData();
  }, []);

  /**
  * Synchronizes home page stats and leaderboard data, ensuring loading state is cleared afterward.
  * @example
  * sync()
  * undefined
  * @returns {Promise<void>} Resolves after fetching stats and leaderboard data and updating state.
  **/
  const fetchHomeData = async () => {
    try {
      const [statsRes, leaderboardRes] = await Promise.all([
        api.get('/api/stats/home').catch(() => ({ data: null })),
        api.get('/api/leaderboard?limit=5').catch(() => ({ data: { users: [] } }))
      ]);

      if (statsRes.data) {
        setStats(statsRes.data);
      }
      setLeaderboard(leaderboardRes.data?.users || []);
    } catch (error) {
      
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#0D1117] text-white overflow-x-hidden">
      <Suspense fallback={<SectionSkeleton height="h-screen" />}>
        <HeroSection currentUser={currentUser} />
      </Suspense>
      <Suspense fallback={<SectionSkeleton height="h-32" />}>
        <LiveStatsBar stats={stats} loading={loading} />
      </Suspense>
      <Suspense fallback={<SectionSkeleton height="h-[600px]" />}>
        <FeaturesSection />
      </Suspense>
      <Suspense fallback={<SectionSkeleton height="h-[500px]" />}>
        <ShowcaseSection />
      </Suspense>
      <Suspense fallback={<SectionSkeleton height="h-96" />}>
        <HowItWorks />
      </Suspense>
      <Suspense fallback={<SectionSkeleton height="h-[400px]" />}>
        <LeaderboardPreview leaderboard={leaderboard} loading={loading} />
      </Suspense>
      <Suspense fallback={<SectionSkeleton height="h-96" />}>
        <TestimonialsSection />
      </Suspense>
      <Suspense fallback={<SectionSkeleton height="h-64" />}>
        <CTASection />
      </Suspense>
      <Suspense fallback={<SectionSkeleton height="h-64" />}>
        <Footer />
      </Suspense>
      <Suspense fallback={null}>
        <FloatingLeaderboard />
      </Suspense>
    </div>
  );
};

export default Home;
